// Activity dates, last payment, and This year / Last 30 days use the firm time zone (America/New_York when unset). Delivered 2026-11-02.
// Client and activity tables sit edge to edge in the card and scroll sideways inside it when the window is narrow. Delivered 2026-11-01.
// pkg193 — Accounting (view_billing): this-year date range, firm totals from GET /api/accounting/summary, sortable searchable per-client table with CSV export, activity from GET /api/accounting/activity (account + client filters). A missing account shows a dash — delivered 2026-10-31
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '../utils/adminApi';
import { firmDateYmd, firmRangeFor, formatFirmDate } from '../utils/firmDate';

const F = { fontFamily: 'Arial, sans-serif' };
const th = { ...F, fontSize: 11, fontWeight: 700, color: '#fff', background: '#0F1838', padding: '8px 10px', textAlign: 'left', whiteSpace: 'nowrap' };
const td = { ...F, fontSize: 13, padding: '8px 10px', borderBottom: '1px solid #eee', verticalAlign: 'middle' };

function rangeFor(preset) {
  return firmRangeFor(preset);
}

function money(n) {
  const num = parseFloat(n);
  const v = Number.isFinite(num) ? num : 0;
  const sign = v < 0 ? '-' : '';
  return sign + '$' + Math.abs(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}
function csvNum(n) {
  const num = parseFloat(n);
  return Number.isFinite(num) ? num.toFixed(2) : '';
}
function csvCell(v) {
  let s = v == null ? '' : String(v);
  if (/^[=+\-@]/.test(s)) s = "'" + s;
  if (/[",\n\r]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
}
function fmtDate(s) {
  return formatFirmDate(s);
}
function humanize(s) {
  if (!s) return '—';
  return String(s).replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}
function AccountBadge({ account }) {
  const base = { ...F, fontSize: 10, fontWeight: 700, letterSpacing: 0.4, padding: '2px 7px', borderRadius: 9, whiteSpace: 'nowrap' };
  if (account === 'operating') return <span style={{ ...base, background: '#e8f1fb', color: '#1c4e80' }}>Operating</span>;
  if (account === 'trust') return <span style={{ ...base, background: '#fff8e1', color: '#8a6a00' }}>Trust</span>;
  return <span style={{ color: '#aaa' }}>—</span>;
}

const PRESETS = [['this-year', 'This year'], ['30', 'Last 30 days'], ['all', 'All time']];

function rangeCaption(from, to) {
  if (!from && !to) return 'Showing all dates.';
  if (from && to) return `Showing ${fmtDate(from)} through ${fmtDate(to)}.`;
  if (from) return `Showing activity on or after ${fmtDate(from)}.`;
  return `Showing activity on or before ${fmtDate(to)}.`;
}

export default function AdminAccountingPage() {
  const navigate = useNavigate();
  const [preset, setPreset] = useState('this-year');
  const [from, setFrom] = useState(() => rangeFor('this-year').from);
  const [to, setTo] = useState(() => rangeFor('this-year').to);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [sumErr, setSumErr] = useState('');
  const [actErr, setActErr] = useState('');
  const [sumLoading, setSumLoading] = useState(true);
  const [actLoading, setActLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('client_name');
  const [sortDir, setSortDir] = useState('asc');
  const [account, setAccount] = useState('');
  const [clientId, setClientId] = useState('');

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    setSumLoading(true);
    setSumErr('');
    adminApi.getAccountingSummary(params)
      .then((data) => { if (!cancelled) setSummary(data || null); })
      .catch((e) => { if (!cancelled) { setSumErr(e.message); setSummary(null); } })
      .finally(() => { if (!cancelled) setSumLoading(false); });
    return () => { cancelled = true; };
  }, [from, to]);

  useEffect(() => {
    let cancelled = false;
    const params = {};
    if (from) params.from = from;
    if (to) params.to = to;
    if (account) params.account = account;
    if (clientId) params.client_id = clientId;
    setActLoading(true);
    setActErr('');
    adminApi.getAccountingActivity(params)
      .then((data) => { if (!cancelled) setRows((data && data.rows) || []); })
      .catch((e) => { if (!cancelled) { setActErr(e.message); setRows([]); } })
      .finally(() => { if (!cancelled) setActLoading(false); });
    return () => { cancelled = true; };
  }, [from, to, account, clientId]);

  function applyPreset(id) {
    const r = rangeFor(id);
    setPreset(id);
    setFrom(r.from);
    setTo(r.to);
  }

  function toggleSort(key) {
    if (sortKey === key) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setSortKey(key);
      setSortDir(key === 'client_name' || key === 'last_payment_date' ? 'asc' : 'desc');
    }
  }

  const firm = summary?.firm || {};
  const clients = summary?.clients || [];
  // If the activity endpoint isn't filtering yet, narrow on the fields it does return.
  const visibleRows = rows.filter((r) => {
    if (account && r.account && r.account !== account) return false;
    if (clientId && r.client_id != null && String(r.client_id) !== String(clientId)) return false;
    return true;
  });

  const visibleClients = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = clients.filter((c) => !q || String(c.client_name || '').toLowerCase().includes(q));
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...list].sort((a, b) => {
      if (sortKey === 'client_name') {
        return dir * String(a.client_name || '').localeCompare(String(b.client_name || ''), undefined, { sensitivity: 'base' });
      }
      if (sortKey === 'last_payment_date') {
        const as = firmDateYmd(a.last_payment_date);
        const bs = firmDateYmd(b.last_payment_date);
        if (!as && !bs) return 0;
        if (!as) return 1;
        if (!bs) return -1;
        return dir * (as < bs ? -1 : as > bs ? 1 : 0);
      }
      const av = parseFloat(a[sortKey]);
      const bv = parseFloat(b[sortKey]);
      return dir * ((Number.isFinite(av) ? av : 0) - (Number.isFinite(bv) ? bv : 0));
    });
  }, [clients, search, sortKey, sortDir]);

  function exportCsv() {
    const header = ['Client', 'Operating received', 'Trust received', 'Trust disbursed', 'Trust balance', 'Last payment'];
    const lines = [header.map(csvCell).join(',')];
    visibleClients.forEach((c) => {
      lines.push([
        csvCell(c.client_name || ''),
        csvNum(c.operating_received),
        csvNum(c.trust_received),
        csvNum(c.trust_disbursed),
        csvNum(c.trust_balance),
        firmDateYmd(c.last_payment_date),
      ].map(csvCell).join(','));
    });
    const blob = new Blob(['\uFEFF' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const span = from || to ? `${from || 'start'}-to-${to || 'end'}` : 'all-time';
    a.href = url;
    a.download = `accounting-clients-${span}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const cards = [
    ['Operating received', firm.operating_received, '#0F1838'],
    ['Trust received', firm.trust_received, '#0F1838'],
    ['Trust disbursed', firm.trust_disbursed, '#0F1838'],
    ['Trust balance', firm.trust_balance, (parseFloat(firm.trust_balance) || 0) < 0 ? '#B85042' : '#1a7a5e'],
    ['Total received', firm.total_received, '#0F1838'],
  ];

  const mark = (k) => (sortKey === k ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '');
  const dateInvalid = from && to && from > to;

  return (
    <div>
      <h1 className="page-title">Accounting</h1>
      <p className="page-subtitle">
        Operating deposits and trust activity together — firm totals, each client, and the individual movements.
        Opens on this calendar year.
      </p>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 10 }}>
          <span style={{ ...F, fontSize: 12, color: '#666' }}>Range</span>
          {PRESETS.map(([id, label]) => (
            <button key={id} type="button" className={`btn btn-sm ${preset === id ? 'btn-primary' : ''}`} onClick={() => applyPreset(id)}>{label}</button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>From</label>
            <input type="date" value={from} onChange={(e) => { setPreset('custom'); setFrom(e.target.value); }} />
          </div>
          <div className="form-group" style={{ margin: 0 }}>
            <label>To</label>
            <input type="date" value={to} onChange={(e) => { setPreset('custom'); setTo(e.target.value); }} />
          </div>
        </div>
        <p style={{ ...F, fontSize: 12, color: '#888', margin: '8px 0 0' }}>{rangeCaption(from, to)}</p>
        {dateInvalid && <p style={{ ...F, fontSize: 12, color: '#B85042', margin: '6px 0 0' }}>The From date is after the To date.</p>}
      </div>

      {sumErr && <div className="alert alert-error">{sumErr}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: '0 0 16px' }}>
        {cards.map(([label, value, color]) => (
          <div key={label} className="card" style={{ padding: 12, margin: 0 }}>
            <div style={{ ...F, fontSize: 10, color: '#888', textTransform: 'uppercase', letterSpacing: '0.04em' }}>{label}</div>
            <div style={{ ...F, fontSize: 20, fontWeight: 700, marginTop: 2, color }}>{sumLoading ? '…' : money(value)}</div>
          </div>
        ))}
      </div>

      <div className="section-head" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>Clients</span>
        <button type="button" className="btn btn-sm" disabled={visibleClients.length === 0} onClick={exportCsv}>Export CSV</button>
      </div>
      <div className="card" style={{ marginBottom: 20, padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 20px 12px' }}>
        <div className="form-group" style={{ marginTop: 0, maxWidth: 320 }}>
          <label>Search clients</label>
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Client name" />
        </div>
        {sumLoading ? <p style={{ ...F, fontSize: 13, color: '#888', margin: 0 }}>Loading…</p> : visibleClients.length === 0 ? (
          <p style={{ ...F, fontSize: 13, color: '#888', margin: 0 }}>
            {clients.length === 0 ? 'No client activity in this window.' : 'No clients match that search.'}
          </p>
        ) : null}
        </div>
        {!sumLoading && visibleClients.length > 0 && (
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={{ ...th, cursor: 'pointer' }} onClick={() => toggleSort('client_name')}>Client{mark('client_name')}</th>
                  <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('operating_received')}>Operating received{mark('operating_received')}</th>
                  <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('trust_received')}>Trust received{mark('trust_received')}</th>
                  <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('trust_disbursed')}>Trust disbursed{mark('trust_disbursed')}</th>
                  <th style={{ ...th, textAlign: 'right', cursor: 'pointer' }} onClick={() => toggleSort('trust_balance')}>Trust balance{mark('trust_balance')}</th>
                  <th style={{ ...th, cursor: 'pointer' }} onClick={() => toggleSort('last_payment_date')}>Last payment{mark('last_payment_date')}</th>
                </tr>
              </thead>
              <tbody>
                {visibleClients.map((c, i) => {
                  const bal = parseFloat(c.trust_balance) || 0;
                  return (
                    <tr key={c.client_id || i}
                      onClick={() => c.client_id && navigate(`/admin/clients/${c.client_id}?tab=billing`)}
                      style={{ cursor: c.client_id ? 'pointer' : 'default', background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...td, fontWeight: 600, color: '#2f5496' }}>{c.client_name || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{money(c.operating_received)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{money(c.trust_received)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace' }}>{money(c.trust_disbursed)}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: bal < 0 ? '#B85042' : bal > 0 ? '#1a7a5e' : '#888' }}>{money(c.trust_balance)}</td>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(c.last_payment_date)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="section-head">Activity</div>
      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '20px 20px 12px' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 10 }}>
          <div className="form-group" style={{ margin: 0 }}>
            <label>Account</label>
            <select value={account} onChange={(e) => setAccount(e.target.value)}>
              <option value="">All</option>
              <option value="operating">Operating</option>
              <option value="trust">Trust</option>
            </select>
          </div>
          <div className="form-group" style={{ margin: 0, minWidth: 200 }}>
            <label>Client</label>
            <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
              <option value="">All clients</option>
              {clients.map((c) => (
                <option key={c.client_id} value={c.client_id}>{c.client_name || 'Client'}</option>
              ))}
            </select>
          </div>
        </div>
        {actErr && <div className="alert alert-error">{actErr}</div>}
        {actLoading ? <p style={{ ...F, fontSize: 13, color: '#888', margin: 0 }}>Loading…</p> : visibleRows.length === 0 ? (
          <p style={{ ...F, fontSize: 13, color: '#888', margin: 0 }}>No activity in this window.</p>
        ) : null}
        </div>
        {!actLoading && visibleRows.length > 0 && (
          <div className="table-wrap">
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  <th style={th}>Date</th>
                  <th style={th}>Client</th>
                  <th style={th}>Account</th>
                  <th style={th}>Type</th>
                  <th style={th}>Description</th>
                  <th style={{ ...th, textAlign: 'right' }}>Amount</th>
                  <th style={th}>Reference</th>
                </tr>
              </thead>
              <tbody>
                {visibleRows.map((r, i) => {
                  const amt = parseFloat(r.amount);
                  return (
                    <tr key={`${r.date}-${r.client_id}-${r.reference}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : '#fafafa' }}>
                      <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</td>
                      <td style={td}>
                        {r.client_id ? (
                          <button type="button" onClick={() => navigate(`/admin/clients/${r.client_id}?tab=billing`)}
                            style={{ background: 'none', border: 'none', padding: 0, color: '#2f5496', cursor: 'pointer', fontWeight: 600, fontSize: 13, fontFamily: 'Arial, sans-serif' }}>
                            {r.client_name || '—'}
                          </button>
                        ) : (r.client_name || '—')}
                      </td>
                      <td style={td}><AccountBadge account={r.account} /></td>
                      <td style={td}>{humanize(r.type)}</td>
                      <td style={td}>{r.description || '—'}</td>
                      <td style={{ ...td, textAlign: 'right', fontFamily: 'monospace', fontWeight: 600, color: Number.isFinite(amt) && amt < 0 ? '#B85042' : '#0F1838' }}>{money(r.amount)}</td>
                      <td style={{ ...td, fontFamily: 'monospace', fontSize: 12 }}>{r.reference || '—'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
